// services/api.js
// Enhanced workspace-aware API service with bot protection, security, email verification, and Google Reviews integration
import webSocketService from './websocket';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

class APIService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('auth_token');
    this.complaintUpdateCallbacks = new Set();
    this.isWebSocketInitialized = false;
    this.currentWorkspace = null;
    this.workspaces = [];
    this.userSubscription = null;
    this.socket = webSocketService;
  }

  // Initialize WebSocket connection
  initializeWebSocket() {
    if (!this.isWebSocketInitialized) {
      webSocketService.connect();
      
      // Set up complaint event listeners
      webSocketService.on('new_complaint', this.handleNewComplaint.bind(this));
      webSocketService.on('complaint_updated', this.handleComplaintUpdate.bind(this));
      webSocketService.on('import_complete', this.handleImportComplete.bind(this));
      webSocketService.on('message_added', this.handleMessageAdded.bind(this));
      
      this.isWebSocketInitialized = true;
    }
  }


  
  // Handle new complaint WebSocket event
  handleNewComplaint(data) {
    console.log('New complaint via WebSocket:', data);
    this.notifyComplaintCallbacks('new', data.data);
  }

  // Handle complaint update WebSocket event
  handleComplaintUpdate(data) {
    console.log('Complaint update via WebSocket:', data);
    this.notifyComplaintCallbacks('update', data.data, data.type);
  }

  // Handle new message added to conversation
  handleMessageAdded(data) {
    console.log('Message added via WebSocket:', data);
    this.notifyComplaintCallbacks('message_added', data.data, data.type);
  }

  // Handle import completion
  handleImportComplete(data) {
    console.log('Import completed:', data);
    this.notifyComplaintCallbacks('import_complete', data.data);
  }

  // Add callback for complaint updates
  onComplaintUpdate(callback) {
    this.complaintUpdateCallbacks.add(callback);
    this.initializeWebSocket();
  }

  // Remove callback for complaint updates
  offComplaintUpdate(callback) {
    this.complaintUpdateCallbacks.delete(callback);
  }

  // Notify all registered callbacks
  notifyComplaintCallbacks(type, complaint, updateType) {
    this.complaintUpdateCallbacks.forEach(callback => {
      try {
        callback({ type, complaint, updateType });
      } catch (error) {
        console.error('Error in complaint update callback:', error);
      }
    });
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('auth_token', token);
    
    if (webSocketService.isSocketConnected()) {
      webSocketService.authenticate(token);
    }
  }

  clearToken() {
    this.token = null;
    this.currentWorkspace = null;
    this.workspaces = [];
    this.userSubscription = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('currentWorkspace');
    localStorage.removeItem('userWorkspaces');
    localStorage.removeItem('userSubscription');
    
    webSocketService.disconnect();
    this.isWebSocketInitialized = false;
  }

  // ===========================
  // SUBSCRIPTION MANAGEMENT METHODS
  // ===========================

  setUserSubscription(subscription) {
    this.userSubscription = subscription;
    localStorage.setItem('userSubscription', JSON.stringify(subscription));
  }

  getUserSubscription() {
    if (!this.userSubscription) {
      const stored = localStorage.getItem('userSubscription');
      if (stored) {
        try {
          this.userSubscription = JSON.parse(stored);
        } catch (error) {
          console.error('Failed to parse stored subscription:', error);
          localStorage.removeItem('userSubscription');
        }
      }
    }
    return this.userSubscription;
  }

  clearUserSubscription() {
    this.userSubscription = null;
    localStorage.removeItem('userSubscription');
  }

  // Helper method to check workspace access
  hasWorkspaceAccess() {
    const subscription = this.getUserSubscription();
    return subscription && ['SCALE', 'ENTERPRISE'].includes(subscription.plan);
  }

  // Helper method to check if user can create workspaces
  canCreateWorkspace() {
    const subscription = this.getUserSubscription();
    if (!subscription) return false;
    
    const hasAccess = this.hasWorkspaceAccess();
    const currentCount = this.getWorkspaces().length;
    const limit = this.getWorkspaceLimit();
    
    return hasAccess && currentCount < limit;
  }

  // Helper method to get workspace limits
  getWorkspaceLimit() {
    const subscription = this.getUserSubscription();
    if (!subscription) return 1;
    
    switch (subscription.plan) {
      case 'FREE':
      case 'STARTER':
      case 'GROWTH':
        return 1;
      case 'SCALE':
        return 5;
      case 'ENTERPRISE':
        return 20;
      default:
        return 1;
    }
  }

  // Helper method to get plan features
  getPlanFeatures() {
    const subscription = this.getUserSubscription();
    if (!subscription) {
      return {
        maxWorkspaces: 1,
        maxUsers: 1,
        hasAdvancedAnalytics: false,
        hasAPIAccess: false,
        hasPrioritySupport: false
      };
    }

    switch (subscription.plan) {
      case 'FREE':
        return {
          maxWorkspaces: 1,
          maxUsers: 1,
          hasAdvancedAnalytics: false,
          hasAPIAccess: false,
          hasPrioritySupport: false
        };
      case 'STARTER':
        return {
          maxWorkspaces: 1,
          maxUsers: 2,
          hasAdvancedAnalytics: false,
          hasAPIAccess: false,
          hasPrioritySupport: false
        };
      case 'GROWTH':
        return {
          maxWorkspaces: 1,
          maxUsers: 5,
          hasAdvancedAnalytics: true,
          hasAPIAccess: false,
          hasPrioritySupport: false
        };
      case 'SCALE':
        return {
          maxWorkspaces: 5,
          maxUsers: 20,
          hasAdvancedAnalytics: true,
          hasAPIAccess: true,
          hasPrioritySupport: true
        };
      case 'ENTERPRISE':
        return {
          maxWorkspaces: 20,
          maxUsers: -1, // unlimited
          hasAdvancedAnalytics: true,
          hasAPIAccess: true,
          hasPrioritySupport: true
        };
      default:
        return this.getPlanFeatures(); // return free plan features
    }
  }

  // ===========================
  // WORKSPACE MANAGEMENT METHODS
  // ===========================

  setCurrentWorkspace(workspace) {
    this.currentWorkspace = workspace;
    localStorage.setItem('currentWorkspace', JSON.stringify(workspace));
  }

  getCurrentWorkspace() {
    if (!this.currentWorkspace) {
      const stored = localStorage.getItem('currentWorkspace');
      if (stored) {
        try {
          this.currentWorkspace = JSON.parse(stored);
        } catch (error) {
          console.error('Failed to parse stored workspace:', error);
          localStorage.removeItem('currentWorkspace');
        }
      }
    }
    return this.currentWorkspace;
  }

  setWorkspaces(workspaces) {
    this.workspaces = workspaces;
    localStorage.setItem('userWorkspaces', JSON.stringify(workspaces));
  }

  getWorkspaces() {
    if (this.workspaces.length === 0) {
      const stored = localStorage.getItem('userWorkspaces');
      if (stored) {
        try {
          this.workspaces = JSON.parse(stored);
        } catch (error) {
          console.error('Failed to parse stored workspaces:', error);
          localStorage.removeItem('userWorkspaces');
        }
      }
    }
    return this.workspaces;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}/api${endpoint}`;
    
    const token = localStorage.getItem('auth_token');
    
    const config = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (options.body) {
      if (options.body instanceof FormData) {
        config.body = options.body;
        delete config.headers['Content-Type'];
      } else if (typeof options.body === 'object') {
        config.body = JSON.stringify(options.body);
      } else {
        config.body = options.body;
      }
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          this.clearToken();
          window.location.href = '/login';
          return;
        }
        
        const errorData = await response.json().catch(() => null);
        const error = new Error(errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`);
        
        // Add additional error properties for better handling
        error.status = response.status;
        error.retryAfter = errorData?.retryAfter;
        error.attempts = errorData?.attempts;
        error.reason = errorData?.reason;
        error.field = errorData?.field;
        
        throw error;
      }
      
      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  async getWorkspacesList() {
    try {
      return await this.request('/workspaces');
    } catch (error) {
      // Return demo workspace for development
      return {
        success: true,
        workspaces: [{
          id: 'demo-workspace',
          name: 'My Workspace',
          description: 'Default workspace',
          slug: 'my-workspace',
          isDefault: true,
          isCurrent: true,
          memberCount: 1
        }],
        subscription: {
          plan: 'FREE',
          status: 'active',
          isActive: true
        }
      };
    }
  }

  async renameWorkspace(workspaceId, name) {
    return this.request(`/workspaces/${workspaceId}/rename`, {
      method: 'PATCH',
      body: { name }
    });
  }

  async getBlockedSenders(workspaceId) {
    return this.request(`/workspaces/${workspaceId}/blocked-senders`);
  }

  async updateBlockedSenders(workspaceId, blockedSenders) {
    return this.request(`/workspaces/${workspaceId}/blocked-senders`, {
      method: 'PUT',
      body: { blockedSenders }
    });
  }

  async switchWorkspace(workspaceId) {
    // Check subscription access before making request
    if (!this.hasWorkspaceAccess() && this.getWorkspaces().length > 1) {
      throw new Error('Multiple workspaces require Scale or Enterprise plan');
    }

    return this.request(`/workspaces/${workspaceId}/switch`, {
      method: 'POST'
    });
  }

  async getCurrentWorkspaceDetails() {
    return this.request('/workspaces/current');
  }

  async createWorkspace(name, description = '', slug = '') {
    // Check subscription access before making request
    if (!this.hasWorkspaceAccess()) {
      throw new Error('Creating workspaces requires Scale or Enterprise plan');
    }

    // Check workspace limit
    if (!this.canCreateWorkspace()) {
      const limit = this.getWorkspaceLimit();
      throw new Error(`Workspace limit (${limit}) reached for your plan`);
    }

    return this.request('/workspaces', {
      method: 'POST',
      body: { name, description, slug }
    });
  }

  async updateWorkspace(workspaceId, data) {
    return this.request(`/workspaces/${workspaceId}`, {
      method: 'PATCH',
      body: data
    });
  }

  async deleteWorkspace(workspaceId) {
    return this.request(`/workspaces/${workspaceId}`, {
      method: 'DELETE'
    });
  }



// ===========================
// FACEBOOK INTEGRATION METHODS
// ===========================

async sendFacebookMessage(complaintId, message, files = []) {
  // If there are files, use FormData
  if (files && files.length > 0) {
    const formData = new FormData();
    formData.append('complaintId', complaintId);
    formData.append('message', message || '');

    // Append all files
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Use fetch directly for FormData
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${this.baseURL}/api/facebook/send-message`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type - browser will set it automatically for FormData
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send Facebook message');
    }

    return response.json();
  }

  // No files - use regular JSON request
  return this.request('/facebook/send-message', {
    method: 'POST',
    body: { complaintId, message }
  });
}

async getFacebookStatus() {
  try {
    return await this.request('/facebook/pages/status');
  } catch (error) {
    console.error('Failed to check Facebook status:', error);
    return { connected: false, pages: [] };
  }
}

async connectFacebook() {
  return this.request('/facebook/connect', {
    method: 'POST'
  });
}

async disconnectFacebook() {
  return this.request('/facebook/disconnect', {
    method: 'DELETE'
  });
}

async getFacebookPages() {
  return this.request('/facebook/pages');
}

async disconnectFacebookPage(pageId) {
  return this.request(`/facebook/pages/${pageId}`, {
    method: 'DELETE'
  });
}

// ===========================
// WHATSAPP INTEGRATION METHODS
// ===========================

async sendWhatsAppMessage(complaintId, message, files = []) {
  // WhatsApp doesn't support multiple files in a single message, send them one by one
  if (files && files.length > 0) {
    // TODO: Implement WhatsApp media sending
    console.warn('WhatsApp media sending not yet implemented');
  }

  // Send text message
  return this.request('/whatsapp/send-message', {
    method: 'POST',
    body: { complaintId, message }
  });
}

async getWhatsAppStatus() {
  try {
    return await this.request('/whatsapp/phone-numbers/status');
  } catch (error) {
    console.error('Failed to check WhatsApp status:', error);
    return { connected: false, phoneNumbers: [] };
  }
}

async connectWhatsApp() {
  return this.request('/whatsapp/connect', {
    method: 'POST'
  });
}

async disconnectWhatsApp() {
  return this.request('/whatsapp/disconnect', {
    method: 'DELETE'
  });
}

async getWhatsAppPhoneNumbers() {
  return this.request('/whatsapp/phone-numbers');
}

async disconnectWhatsAppNumber(phoneNumberId) {
  return this.request(`/whatsapp/phone-numbers/${phoneNumberId}`, {
    method: 'DELETE'
  });
}

  // ===========================
  // WEBHOOK TESTING METHODS
  // ===========================

  async testWebhookEndpoint() {
    return this.request('/webhooks/mailgun/test', {
      method: 'GET'
    });
  }

  async testResendWebhookEndpoint() {
    return this.request('/webhooks/resend/test', {
      method: 'GET'
    });
  }

  // ===========================
  // CONVERSATION METHODS
  // ===========================

  async improveText(text, options = {}) {
    try {
      return await this.request('/complaints/improve-text', {
        method: 'POST',
        body: { 
          text,
          context: options.context || 'customer_service_reply',
          customerName: options.customerName || 'Customer',
          subject: options.subject || ''
        }
      });
    } catch (error) {
      console.error('Failed to improve text:', error);
      return {
        success: true,
        improvedText: this.improveFallback(text, options.customerName)
      };
    }
  }

  improveFallback(text, customerName = 'Customer') {
    let improved = text.trim();
    
    improved = improved.charAt(0).toUpperCase() + improved.slice(1);
    
    if (!improved.match(/[.!?]$/)) {
      improved += '.';
    }
    
    if (!improved.toLowerCase().startsWith('dear') && 
        !improved.toLowerCase().startsWith('hello') && 
        !improved.toLowerCase().startsWith('hi')) {
      improved = `Dear ${customerName},\n\n${improved}`;
    }
    
    if (!improved.toLowerCase().includes('regards') && 
        !improved.toLowerCase().includes('sincerely')) {
      improved += '\n\nBest regards,\nCustomer Service Team';
    }
    
    return improved;
  }

  async getConversation(complaintId) {
    return this.request(`/complaints/${complaintId}/conversation`);
  }

  async addConversationMessage(complaintId, content, isInternal = false, files = []) {
    // If there are files, use FormData
    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('isInternal', isInternal);
      formData.append('messageType', isInternal ? 'INTERNAL_NOTE' : 'OUTBOUND');

      // Append all files
      files.forEach((file) => {
        formData.append('images', file);
      });

      // Use fetch directly for FormData (don't set Content-Type header - browser will set it with boundary)
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/api/complaints/${complaintId}/conversation/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - browser will set it automatically for FormData
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send message');
      }

      return response.json();
    }

    // No files - use regular JSON request
    return this.request(`/complaints/${complaintId}/conversation/message`, {
      method: 'POST',
      body: {
        content,
        isInternal,
        messageType: isInternal ? 'INTERNAL_NOTE' : 'OUTBOUND'
      }
    });
  }

  async markMessagesAsRead(complaintId, messageIds) {
    return this.request(`/complaints/${complaintId}/conversation/read`, {
      method: 'PATCH',
      body: { messageIds }
    });
  }

  async sendReply(complaintId, content) {
    return this.addConversationMessage(complaintId, content, false);
  }

  async addInternalNote(complaintId, content) {
    return this.addConversationMessage(complaintId, content, true);
  }

  // ===========================
  // GMAIL INTEGRATION METHODS
  // ===========================

  async checkGmailStatus() {
    return this.request('/auth/gmail/status');
  }

  async getGmailImportStatus() {
    return this.request('/auth/gmail/import-status');
  }

  async connectGmail() {
    return this.request('/auth/gmail/connect', {
      method: 'POST'
    });
  }

  async disconnectGmail() {
    return this.request('/auth/gmail/disconnect', {
      method: 'DELETE'
    });
  }

  async importGmailComplaints(maxResults = 50) {
    return this.request('/auth/gmail/import', {
      method: 'POST',
      body: { maxResults }
    });
  }

  async getGmailImportHistory() {
    return this.request('/auth/gmail/import-history');
  }

  async getGmailStatus() {
    return this.request('/auth/gmail/status');
  }

  // ===========================
  // TRIAL MANAGEMENT ENDPOINTS
  // ===========================

  async getTrialStatus() {
    try {
      return await this.request('/trial/status');
    } catch (error) {
      return {
        isInTrial: true,
        daysLeft: 7,
        isExpired: false,
        hasActiveSubscription: false
      };
    }
  }

  async getPlans() {
    try {
      return await this.request('/trial/plans');
    } catch (error) {
      return [
        {
          id: 'free',
          name: 'Free',
          price: 0,
          priceId: null,
          complaints: 10,
          features: ['10 complaints/month', 'Basic email support', 'Single user', '1 workspace'],
          popular: false
        },
        {
          id: 'starter',
          name: 'Starter',
          price: 29,
          priceId: 'price_starter',
          complaints: 50,
          features: ['50 complaints/month', 'Gmail integration', 'AI responses', 'Email support', '1 workspace'],
          popular: false,
          trial: true
        },
        {
          id: 'growth',
          name: 'Growth',
          price: 79,
          priceId: 'price_growth',
          complaints: 500,
          features: ['500 complaints/month', 'Priority support', 'Up to 5 users', 'Advanced analytics', '1 workspace'],
          popular: true
        },
        {
          id: 'scale',
          name: 'Scale',
          price: 199,
          priceId: 'price_scale',
          complaints: 2000,
          features: ['2000 complaints/month', 'Priority support', 'Up to 20 users', 'API access', 'Multiple workspaces (5)'],
          popular: false
        },
        {
          id: 'enterprise',
          name: 'Enterprise',
          price: 499,
          priceId: 'price_enterprise',
          complaints: -1,
          features: ['Unlimited complaints', 'Priority support', 'Unlimited users', 'API access', 'Multiple workspaces (20)'],
          popular: false
        }
      ];
    }
  }

  async createCheckoutSession(data) {
    return this.request('/subscription/create-checkout', {
      method: 'POST',
      body: data
    });
  }

  async downgradeToFree() {
    return this.request('/subscription/cancel', {
      method: 'POST'
    });
  }

  // ===========================
  // EMAIL VERIFICATION METHODS  
  // ===========================

  async validateEmail(email) {
    try {
      return await this.request('/auth/validate-email', {
        method: 'POST',
        body: { email }
      });
    } catch (error) {
      console.error('Email validation failed:', error);
      return {
        isValid: false,
        message: 'Email validation service unavailable'
      };
    }
  }

async verifyEmail(token) {
  try {
    return await this.request(`/auth/verify-email/${token}`, {
      method: 'GET'
    });
  } catch (error) {
    console.error('Email verification failed:', error);
    throw error;
  }
}

  async resendVerificationEmail(email) {
    try {
      return await this.request('/auth/resend-verification', {
        method: 'POST',
        body: { email }
      });
    } catch (error) {
      console.error('Resend verification failed:', error);
      throw error;
    }
  }

  // ===========================
  // ANALYTICS METHODS
  // ===========================

  // ===========================
// ANALYTICS METHODS
// ===========================

async getAnalyticsOverview(dateRange = '30d') {
  return this.request(`/analytics/overview?range=${dateRange}`);
}

async getAnalyticsReviews(dateRange = '30d') {
  return this.request(`/analytics/reviews?range=${dateRange}`);
}

async getAnalyticsTickets(dateRange = '30d') {
  return this.request(`/analytics/tickets?range=${dateRange}`);
}

async getAnalyticsTeamPerformance(dateRange = '30d') {
  return this.request(`/analytics/team-performance?range=${dateRange}`);
}

async getAnalyticsTrends(dateRange = '30d') {
  return this.request(`/analytics/trends?range=${dateRange}`);
}

async getAnalyticsRatingDistribution(dateRange = '30d') {
  return this.request(`/analytics/rating-distribution?range=${dateRange}`);
}

async sendWeeklyReport() {
  return this.request('/analytics/send-weekly-report', {
    method: 'POST'
  });
}

async exportAnalyticsPDF(dateRange = '30d') {
  const url = `${API_BASE_URL}/api/analytics/export-pdf?range=${dateRange}`;
  const token = localStorage.getItem('auth_token');
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) throw new Error('Failed to export PDF');
  
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `analytics-report-${dateRange}-${new Date().toISOString().split('T')[0]}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
}


  async getComplaintsByCategory(dateRange = '30d') {
    return this.request(`/analytics/complaints-by-category?range=${dateRange}`);
  }

  async getComplaintsVolume(dateRange = '30d') {
    return this.request(`/analytics/complaints-volume?range=${dateRange}`);
  }

  async getResolutionStats(dateRange = '30d') {
    return this.request(`/analytics/resolution-stats?range=${dateRange}`);
  }

  async getTopKeywords(dateRange = '30d') {
    return this.request(`/analytics/top-keywords?range=${dateRange}`);
  }
// ===========================
// USAGE TRACKING METHODS
// ===========================

async getUsage() {
  try {
    return await this.request('/subscription/usage');
  } catch (error) {
    console.error('Failed to fetch usage:', error);
    return {
      plan: 'FREE',
      limit: 10,
      used: 0,
      remaining: 10,
      percentageUsed: 0,
      warningLevel: 'none'
    };
  }
}

async getUsageHistory(months = 6) {
  try {
    return await this.request(`/subscription/usage/history?months=${months}`);
  } catch (error) {
    console.error('Failed to fetch usage history:', error);
    return { history: [] };
  }
}

async getEnhancedSubscriptionStatus() {
  try {
    return await this.request('/subscription/status-enhanced');
  } catch (error) {
    console.error('Failed to fetch enhanced subscription status:', error);
    return {
      status: 'none',
      plan: 'FREE',
      usage: {
        limit: 10,
        used: 0,
        remaining: 10,
        percentageUsed: 0,
        warningLevel: 'none'
      }
    };
  }
}
  // ===========================
  // AUTH METHODS (ENHANCED WITH SECURITY AND VERIFICATION)
  // ===========================

  async login(email, password, securityData = {}) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      headers: {
        'X-Timestamp': securityData.botProtection?.timestamp?.toString() || '',
        'X-Interactions': securityData.botProtection?.interactionCount?.toString() || ''
      },
      body: {
        email,
        password,
        botProtection: securityData.botProtection
      }
    });

    if (response.token) {
      this.setToken(response.token);
      
      if (response.workspace) {
        this.setCurrentWorkspace(response.workspace);
      }
      
      if (response.subscription) {
        this.setUserSubscription(response.subscription);
      }
      
      this.initializeWebSocket();
    }
    return response;
  }

  async register(userData) {
    const response = await this.request('/auth/register', {
      method: 'POST',
      headers: {
        'X-Timestamp': userData.botProtection?.timestamp?.toString() || '',
        'X-Interactions': userData.botProtection?.interactionCount?.toString() || ''
      },
      body: userData
    });

    // Don't set token if email verification is required
    if (response.requiresVerification) {
      return response;
    }

    if (response.token) {
      this.setToken(response.token);
      
      if (response.workspace) {
        this.setCurrentWorkspace(response.workspace);
      }
      
      if (response.subscription) {
        this.setUserSubscription(response.subscription);
      }
      
      this.initializeWebSocket();
    }
    return response;
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async completeOnboarding() {
    return this.request('/auth/onboarding/complete', {
      method: 'POST'
    });
  }

  async logout() {
    this.clearToken();
    window.location.href = '/login';
  }

  // ===========================
  // COMPLAINTS METHODS
  // ===========================

  async getComplaints() {
    return await this.request('/complaints');
  }

  async getComplaint(id) {
    return this.request(`/complaints/${id}`);
  }

  async updateComplaintStatus(id, status) {
    try {
      return await this.request(`/complaints/${id}/status`, {
        method: 'PATCH',
        body: { status }
      });
    } catch (error) {
      return { success: true };
    }
  }

  async updateComplaint(id, data) {
    return this.request(`/complaints/${id}`, {
      method: 'PUT',
      body: data
    });
  }

  async deleteComplaint(id) {
    return this.request(`/complaints/${id}`, {
      method: 'DELETE'
    });
  }

  async updateComplaintAssignee(complaintId, userId) {
    try {
      return await this.request(`/complaints/${complaintId}/assign`, {
        method: 'PATCH',
        body: { assignedTo: userId }
      });
    } catch (error) {
      console.error('Failed to update assignee:', error);
      const assignments = JSON.parse(localStorage.getItem('complaint_assignments') || '{}');
      assignments[complaintId] = userId;
      localStorage.setItem('complaint_assignments', JSON.stringify(assignments));
      return { success: true };
    }
  }

  async updateComplaintPriority(complaintId, priority) {
    try {
      return await this.request(`/complaints/${complaintId}/priority`, {
        method: 'PATCH',
        body: { priority }
      });
    } catch (error) {
      console.error('Failed to update priority:', error);
      const priorities = JSON.parse(localStorage.getItem('complaint_priorities') || '{}');
      priorities[complaintId] = priority;
      localStorage.setItem('complaint_priorities', JSON.stringify(priorities));
      return { success: true };
    }
  }

  async getCustomerHistory(customerId) {
    try {
      const response = await this.request(`/complaints/customer/${customerId}`);
      if (response && response.complaints) {
        return response.complaints;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch customer history:', error);
      return [];
    }
  }

  // ===========================
  // AI AND RESPONSE METHODS
  // ===========================

  async generateAIResponse(complaintId, templateContent = null) {
    try {
      return await this.request(`/complaints/${complaintId}/ai-response`, {
        method: 'POST',
        body: { templateContent }
      });
    } catch (error) {
      return {
        success: true,
        suggestion: "Thank you for bringing this to our attention. We sincerely apologize for any inconvenience you've experienced. Our team is reviewing your complaint and will provide a resolution within 24-48 hours. We value your patience and your business."
      };
    }
  }

  async sendResponse(complaintId, responseContent) {
    return this.sendReply(complaintId, responseContent);
  }

  // ===========================
  // TEMPLATE METHODS
  // ===========================

  async getResponseTemplates() {
    return this.request('/templates/responses');
  }

  async saveResponseTemplate(template) {
    return this.request('/templates/responses', {
      method: 'POST',
      body: template
    });
  }

  async updateResponseTemplate(id, template) {
    return this.request(`/templates/responses/${id}`, {
      method: 'PUT',
      body: template
    });
  }

  async deleteResponseTemplate(id) {
    return this.request(`/templates/responses/${id}`, {
      method: 'DELETE'
    });
  }

  async improveTemplate(template) {
    return this.request('/templates/improve', {
      method: 'POST',
      body: { 
        content: template.content,
        name: template.name,
        category: template.category 
      }
    });
  }

  // ===========================
  // SETTINGS METHODS
  // ===========================

  async getSettings() {
    return this.request('/auth/settings');
  }

  async updateSettings(settings) {
    return this.request('/auth/settings', {
      method: 'PATCH',
      body: settings
    });
  }

  async saveTemplates(templates) {
    return this.request('/templates/save', {
      method: 'POST',
      body: templates
    });
  }

  async getTemplates() {
    return this.request('/templates');
  }

  async updateBusinessHours(hours) {
    return this.request('/settings/business-hours', {
      method: 'PUT',
      body: hours
    });
  }

  async uploadLogo(file) {
    const formData = new FormData();
    formData.append('logo', file);
    
    return this.request('/settings/logo', {
      method: 'POST',
      headers: {},
      body: formData
    });
  }
  // ===========================
  // AUTORESPOND METHODS
  // ===========================

async getAutoResponseSettings() {
  return this.request('/auth/auto-response/settings');
}

async updateAutoResponseSettings(settings) {
  return this.request('/auth/auto-response/settings', {
    method: 'PUT',
    body: { settings }
  });
}
  // ===========================
  // TEAM MANAGEMENT METHODS
  // ===========================

  async getTeamMembers() {
    return this.request('/auth/team');
  }

  async inviteTeamMember(email, role = 'AGENT', name = '') {
    return this.request('/auth/team/invite', {
      method: 'POST',
      body: { email, role, name }
    });
  }

  async resendInvitation(inviteId) {
    return this.request(`/auth/team/invite/${inviteId}/resend`, {
      method: 'POST'
    });
  }

  async revokeInvitation(inviteId) {
    return this.request(`/auth/team/invite/${inviteId}`, {
      method: 'DELETE'
    });
  }

  async removeTeamMember(userId) {
    return this.request(`/auth/team/${userId}`, {
      method: 'DELETE'
    });
  }

  async acceptInvitation(token, password, name) {
    return this.request(`/auth/accept-invitation/${token}`, {
      method: 'POST',
      body: { password, name }
    });
  }

  // ===========================
  // PROFILE MANAGEMENT METHODS
  // ===========================

  async updateProfile(profileData) {
    return this.request('/auth/profile', {
      method: 'PATCH',
      body: profileData
    });
  }

  async changePassword(passwordData) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: passwordData
    });
  }

// ===========================
// TRUSTPILOT INTEGRATION METHODS (SCRAPER-BASED)
// REPLACE YOUR EXISTING TRUSTPILOT METHODS WITH THESE
// ===========================
async updateReviewStatus(reviewId, status) {
  return this.request(`/reviews/${reviewId}/status`, {
    method: 'PATCH',
    body: { status }
  });
}

async generateReviewResponse(reviewId) {
  return this.request(`/reviews/${reviewId}/generate-response`, {
    method: 'POST'
  });
}

async updateWorkspaceTrustpilotDomain(domain) {
  return this.request('/reviews/trustpilot/workspace/domain', {
    method: 'POST',
    body: { domain }
  });
}

async removeTrustpilotDomain(deleteReviews = false) {
  const queryParam = deleteReviews ? '?deleteReviews=true' : '';
  return this.request(`/reviews/trustpilot/workspace/domain${queryParam}`, {
    method: 'DELETE'
  });
}

async getTrustpilotStatus() {
  try {
    return await this.request('/reviews/trustpilot/status');
  } catch (error) {
    console.error('Failed to check Trustpilot status:', error);
    return { connected: false, locations: [] };
  }
}

// Add Trustpilot domain to a business location
async addTrustpilotDomain(locationId, domain) {
  return this.request(`/reviews/trustpilot/locations/${locationId}/domain`, {
    method: 'POST',
    body: { domain }
  });
}



// Verify a Trustpilot domain exists
async verifyTrustpilotDomain(domain) {
  return this.request('/reviews/trustpilot/verify-domain', {
    method: 'POST',
    body: { domain }
  });
}

// Sync specific location
async syncTrustpilotLocation(locationId) {
  return this.request(`/reviews/trustpilot/locations/${locationId}/sync`, {
    method: 'POST'
  });
}

// Sync all workspace Trustpilot locations
async syncTrustpilotReviews() {
  return this.request('/reviews/trustpilot/sync', {
    method: 'POST'
  });
}

// KEEP EXISTING METHODS (already correct)
async getReviews(locationId = null, filters = {}) {
  const params = new URLSearchParams();
  
  if (locationId) {
    params.append('locationId', locationId);
  }
  
  if (filters.platform) {
    params.append('platform', filters.platform);
  }
  
  if (filters.startDate) {
    params.append('startDate', filters.startDate);
  }
  
  if (filters.endDate) {
    params.append('endDate', filters.endDate);
  }
  
  if (filters.rating) {
    params.append('rating', filters.rating);
  }
  
  if (filters.responded !== undefined) {
    params.append('responded', filters.responded);
  }

  const queryString = params.toString();
  const endpoint = queryString ? `/reviews?${queryString}` : '/reviews';
  
  return this.request(endpoint);
}

async getAnalyticsSettings() {
  return this.request('/analytics/settings');
}

async updateAnalyticsSettings(settings) {
  return this.request('/analytics/settings', {
    method: 'PUT',
    body: { settings }
  });
}

async getReviewsAnalytics(dateRange = '30d', platform = 'all') {
  return this.request(`/reviews/stats?range=${dateRange}&platform=${platform}`);
}

async getReviewSettings() {
  return this.request('/reviews/settings');
}

async updateReviewSettings(settings) {
  return this.request('/reviews/settings', {
    method: 'PUT',
    body: { settings }
  });
}

// ===========================
// G2 INTEGRATION METHODS
// ===========================
async getG2Status() {
  try {
    return await this.request('/reviews/g2/status');
  } catch (error) {
    console.error('Failed to check G2 status:', error);
    return { connected: false };
  }
}

async verifyG2Product(productSlug) {
  return this.request('/reviews/g2/verify-product', {
    method: 'POST',
    body: { productSlug }
  });
}

async updateWorkspaceG2Product(productSlug) {
  return this.request('/reviews/g2/workspace/product', {
    method: 'POST',
    body: { productSlug }
  });
}

async removeG2Product(deleteReviews = false) {
  const queryParam = deleteReviews ? '?deleteReviews=true' : '';
  return this.request(`/reviews/g2/workspace/product${queryParam}`, {
    method: 'DELETE'
  });
}

async syncG2Reviews() {
  return this.request('/reviews/g2/sync', {
    method: 'POST'
  });
}

// ===========================
// CAPTERRA INTEGRATION METHODS
// ===========================
async getCapterraStatus() {
  try {
    return await this.request('/reviews/capterra/status');
  } catch (error) {
    console.error('Failed to check Capterra status:', error);
    return { connected: false };
  }
}

async verifyCapterraProduct(productSlug) {
  return this.request('/reviews/capterra/verify-product', {
    method: 'POST',
    body: { productSlug }
  });
}

async updateWorkspaceCapterraProduct(productSlug) {
  return this.request('/reviews/capterra/workspace/product', {
    method: 'POST',
    body: { productSlug }
  });
}

async removeCapterraProduct(deleteReviews = false) {
  const queryParam = deleteReviews ? '?deleteReviews=true' : '';
  return this.request(`/reviews/capterra/workspace/product${queryParam}`, {
    method: 'DELETE'
  });
}

async syncCapterraReviews() {
  return this.request('/reviews/capterra/sync', {
    method: 'POST'
  });
}

  // ===========================
  // DEMO DATA METHODS
  // ===========================

  getDemoComplaints() {
    return [
      {
        id: 'demo1',
        customer_name: 'John Smith',
        customer_email: 'john.smith@example.com',
        subject: 'Product Quality Issue',
        description: 'The product I received was damaged during shipping. The packaging was torn and the item inside was broken.',
        status: 'NEW',
        priority: 'HIGH',
        source: 'email',
        created_at: new Date(Date.now() - 3600000).toISOString(),
        updated_at: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: 'demo2',
        customer_name: 'Sarah Johnson',
        customer_email: 'sarah.j@example.com',
        subject: 'Billing Discrepancy',
        description: 'I was charged twice for my last order. Please refund the duplicate charge.',
        status: 'IN_PROGRESS',
        priority: 'URGENT',
        source: 'email',
        created_at: new Date(Date.now() - 7200000).toISOString(),
        updated_at: new Date(Date.now() - 1800000).toISOString()
      },
      {
        id: 'demo3',
        customer_name: 'Mike Wilson',
        customer_email: 'mike.w@example.com',
        subject: 'Late Delivery',
        description: 'My order was supposed to arrive 3 days ago but I still haven\'t received it.',
        status: 'NEW',
        priority: 'MEDIUM',
        source: 'email',
        created_at: new Date(Date.now() - 86400000).toISOString(),
        updated_at: new Date(Date.now() - 86400000).toISOString()
      }
    ];
  }

  getDemoTemplates() {
    return [
      { 
        id: 'tpl_general', 
        name: 'General Response', 
        content: 'Thank you for contacting us. We have received your complaint and will respond within 24 hours.',
        category: 'general'
      },
      { 
        id: 'tpl_billing', 
        name: 'Billing Issue', 
        content: 'We apologize for the billing issue. Our accounting team will review your account and provide a resolution within 2 business days.',
        category: 'billing'
      },
      { 
        id: 'tpl_shipping', 
        name: 'Shipping Delay', 
        content: 'We sincerely apologize for the shipping delay. We are tracking your package and will provide an update shortly.',
        category: 'shipping'
      },
      { 
        id: 'tpl_product', 
        name: 'Product Defect', 
        content: 'We are sorry to hear about the product quality issue. We will arrange a replacement or refund as appropriate.',
        category: 'product'
      },
      { 
        id: 'tpl_refund', 
        name: 'Refund Request', 
        content: 'We have processed your refund request. The amount will be credited to your original payment method within 5-7 business days.',
        category: 'refund'
      },
      { 
        id: 'tpl_escalation', 
        name: 'Escalation', 
        content: 'Your complaint has been escalated to our senior management team. A supervisor will contact you within 24 hours.',
        category: 'escalation'
      },
      { 
        id: 'tpl_resolution', 
        name: 'Resolution', 
        content: 'We are pleased to inform you that your issue has been resolved. Please let us know if you need any further assistance.',
        category: 'resolution'
      }
    ];
  }
}



// Export singleton instance
const apiService = new APIService();
export default apiService;